import * as React from 'react';
import { cn } from '../lib/utils';

export interface PageHeaderProps {
  title: string;
  description?: string;
  /** Primary actions, rendered on the right. */
  actions?: React.ReactNode;
  /**
   * `brand`: the deep blue band of the home — white type over the brand gradient, with room at
   * the bottom for content that overlaps its edge (the day's numbers). Everything else stays
   * `default`.
   */
  tone?: 'default' | 'brand';
  className?: string;
}

/** Consistent page header: title + description on the left, primary actions on the right. */
function PageHeader({ title, description, actions, tone = 'default', className }: PageHeaderProps) {
  const brand = tone === 'brand';
  return (
    <div
      data-slot="page-header"
      data-tone={tone}
      className={cn(
        'flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between',
        brand &&
          'relative overflow-hidden rounded-2xl bg-[linear-gradient(135deg,var(--brand)_0%,var(--brand-deep)_100%)] px-6 py-7 text-brand-foreground shadow-md sm:px-8',
        className,
      )}
    >
      {brand ? (
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-16 size-72 rounded-full bg-white/10 blur-3xl"
        />
      ) : null}
      <div className="relative min-w-0 space-y-1">
        <h1
          className={cn('text-2xl font-semibold tracking-tight text-balance', brand && 'text-3xl')}
        >
          {title}
        </h1>
        {description ? (
          <p
            className={cn('text-sm', brand ? 'text-brand-foreground/75' : 'text-muted-foreground')}
          >
            {description}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div className="relative flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
      ) : null}
    </div>
  );
}

export { PageHeader };
